/**
 * 사용자(/app) 셸 — 랜딩(livegpt.co.kr)과 같은 브랜드 머리와 문의·사업자 바닥 (260917-admin-area-split).
 * 브랜드 하나든 여럿이든 같은 셸이다 (261013-account-brand-membership — 대행사 콘솔을 합쳤다).
 * ★ 본문 컨테이너 클래스(`space-y-4` 등)는 영역 분리 전과 같다 — 화면 본문이 여기에 기댄다.
 * ★ SUIT 는 머리·바닥에만 — 본문(표 숫자 폭)은 바꾸지 않는다.
 */
import type { Me } from '@geo-admin/shared/contract/api/auth.ts';
import { article } from '@geo-admin/shared/contract/api/index.ts';
import { Link } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useApi } from '../lib/api.ts';
import { SHELL, WHITE_LABEL } from '../lib/branding.ts';
import { impersonation } from '../lib/impersonation.ts';
import { useLogout } from '../lib/me.ts';
import { APP_NAV } from '../lib/nav.ts';
import { PATHS } from '../lib/paths.ts';
import { endImpersonation, ImpersonationBanner } from './ImpersonationBanner.tsx';
import { KakaoIcon } from './icons.tsx';
import { LogoMark } from './LogoMark.tsx';
import { Button } from './ui/button.tsx';

export function AppShell({ me, children }: { me: Me; children: ReactNode }) {
  const logout = useLogout();
  const imp = impersonation();
  const articlesPath = '/app/articles';
  // 검토를 기다리는 기사(보낸 상태) 수 — 탭 우측 위 빨간 숫자. 승인하면 쓰기 훅이 캐시를 무효화해 바로 줄어든다
  const reviews = useApi(article.listReviewArticles, {}, { refetchInterval: 60_000 });
  const pending = reviews.data?.articles.filter((a) => a.status === 'sent').length ?? 0;

  return (
    <div className="flex min-h-screen flex-col">
      {imp ? (
        <ImpersonationBanner targetEmail={me.email} adminEmail={me.impersonator?.email ?? imp.adminEmail} />
      ) : null}
      <header className="sticky top-0 z-30 border-slate-200 border-b bg-white/95 font-suit backdrop-blur">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-x-6 gap-y-2 px-4 py-2.5">
          <Link to={PATHS.app.home} aria-label="첫 화면으로">
            <LogoMark tone="onLight" size="sm" />
          </Link>
          {/* p-1 -m-1 — 숫자 배지가 탭 밖으로 살짝 나가는데 overflow-x-auto 가 잘라 먹지 않게 */}
          <nav className="order-last -m-1 flex w-full gap-1 overflow-x-auto p-1 sm:order-none sm:w-auto">
            {APP_NAV.filter((n) => !(n.to === '/app/billing' && me.agencyRole === 'client')).map((n) => (
              <Link
                key={n.to}
                to={n.to}
                className="relative whitespace-nowrap rounded-lg px-3 py-1.5 font-medium text-[15px] text-slate-600 transition-colors hover:bg-slate-100 hover:text-slate-900"
                activeProps={{ className: 'bg-live/10 text-live hover:bg-live/10 hover:text-live' }}
              >
                {n.label}
                {n.to === articlesPath && pending > 0 ? (
                  <span
                    className="-right-1 -top-1 absolute grid h-[18px] min-w-[18px] place-items-center rounded-full bg-red-500 px-1 font-bold text-[11px] text-white leading-none ring-2 ring-white"
                    title={`검토할 기사 ${pending}건`}
                  >
                    {pending > 99 ? '99+' : pending}
                    <span className="sr-only">건 검토 대기</span>
                  </span>
                ) : null}
              </Link>
            ))}
          </nav>
          <div className="ml-auto flex min-w-0 items-center gap-2 text-sm">
            <span className="hidden min-w-0 max-w-56 truncate text-slate-500 md:inline" title={me.email}>
              {me.email}
            </span>
            {imp ? (
              <Button size="sm" variant="outline" onClick={() => void endImpersonation()}>
                대리 종료
              </Button>
            ) : (
              <>
                <Link
                  to="/app/account"
                  className="px-1 text-slate-500 hover:text-slate-900"
                  activeProps={{ className: 'text-live' }}
                >
                  계정
                </Link>
                <Button size="sm" variant="outline" onClick={() => void logout(me)}>
                  로그아웃
                </Button>
              </>
            )}
          </div>
        </div>
      </header>
      <main className="mx-auto w-full max-w-7xl flex-1 space-y-4 px-4 py-6">{children}</main>
      <SiteFooter />
    </div>
  );
}

/** 바닥 — 화이트라벨이면 대행사 연락처·사업자 정보·약관(261025-agency-white-label). 값이 없는 칸은 숨긴다 */
function SiteFooter() {
  const { company, support } = SHELL;
  const legalLine = [
    company.name,
    company.ceo && `대표 ${company.ceo}`,
    company.registrationNo && `사업자등록번호 ${company.registrationNo}`,
    company.mailOrderNo && `통신판매업 ${company.mailOrderNo}`,
    company.address,
  ]
    .filter(Boolean)
    .join(' · ');
  const hasContact = support.tel || support.kakaoUrl || support.email;
  return (
    <footer className="border-slate-200 border-t bg-white font-suit">
      <div className="mx-auto max-w-7xl space-y-3 px-4 py-6 text-slate-500 text-xs">
        {hasContact && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm">
            <span className="font-semibold text-slate-700">문의</span>
            {support.tel && (
              <a href={`tel:${support.tel.replace(/\D/g, '')}`} className="hover:text-slate-900">
                대표번호 <b className="text-slate-800">{support.tel}</b>
              </a>
            )}
            {support.kakaoUrl && (
              <a
                href={support.kakaoUrl}
                target="_blank"
                rel="noreferrer"
                className="inline-flex items-center gap-1.5 rounded-full bg-kakao px-3 py-1 font-semibold text-[#191919] text-[13px] hover:brightness-95"
              >
                <KakaoIcon className="h-4 w-4" />
                카카오톡 상담
              </a>
            )}
            {support.email && (
              <a href={`mailto:${support.email}`} className="hover:text-slate-900">
                {support.email}
              </a>
            )}
          </div>
        )}
        <p className="leading-relaxed">{legalLine}</p>
        <p className="flex flex-wrap gap-x-3 gap-y-1">
          {WHITE_LABEL ? (
            <>
              {SHELL.termsUrl && (
                <a href={SHELL.termsUrl} target="_blank" rel="noreferrer" className="hover:text-slate-900">
                  이용약관
                </a>
              )}
              {SHELL.privacyUrl && (
                <a
                  href={SHELL.privacyUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="font-semibold hover:text-slate-900"
                >
                  개인정보처리방침
                </a>
              )}
            </>
          ) : (
            <>
              <Link to="/terms" target="_blank" className="hover:text-slate-900">
                이용약관
              </Link>
              <Link to="/privacy" target="_blank" className="font-semibold hover:text-slate-900">
                개인정보처리방침
              </Link>
            </>
          )}
          {SHELL.homepage && (
            <a href={SHELL.homepage} target="_blank" rel="noreferrer" className="hover:text-slate-900">
              {SHELL.serviceName} 소개
            </a>
          )}
        </p>
      </div>
    </footer>
  );
}
